import { useEffect, useState, useMemo, useRef } from "react";
import {
  augcRecords as mockAugcRecords,
  augcHistoryStats as mockAugcHistoryStats,
  type AUGCRecord,
} from "@/mocks/augcHistory";
import { useApi } from "@/hooks/useApi";
import { ApiError, apiGet, apiSend } from "@/lib/api";
import { pageWindow } from "@/lib/pagination";
import { toast } from "@/components/ui/feedback";
import RegenerateRecordDialog from "@/components/RegenerateRecordDialog";
import {
  isQwenModel,
  persistPendingRegeneration,
  readPendingRegenerations,
  type PendingRegeneration,
  type RegenerationResponse,
} from "@/services/generationOptions";
interface AUGCRecordsPage {
  items: AUGCRecord[];
  total: number;
  page: number;
  limit: number;
}

type SearchField = "template" | "session" | "deviceNo" | "slotCode";

const searchFieldOptions: Array<{ value: SearchField; label: string; placeholder: string }> = [
  { value: "template", label: "模板名称", placeholder: "搜索模板名称" },
  { value: "session", label: "会话编号", placeholder: "搜索会话编号" },
  { value: "deviceNo", label: "设备编号", placeholder: "搜索设备编号" },
  { value: "slotCode", label: "槽位编码", placeholder: "搜索槽位编码" },
];

const mockRecordsPage: AUGCRecordsPage = {
  items: mockAugcRecords,
  total: mockAugcRecords.length,
  page: 1,
  limit: 10,
};


// Download via a freshly minted signed URL (the one embedded in the list
// expires ~30 min after fetch; the job status endpoint re-signs on demand).
async function downloadRecordPhoto(record: AUGCRecord) {
  if ((record.stage && record.stage !== "published") || record.status !== "success") return;
  try {
    const st = await apiGet<{ photoUrl?: string }>(`/vision/face-swap/${record.id}`);
    const url = st.photoUrl ?? record.imageUrl;
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${record.templateName || "faceswap"}_${record.id.slice(-6)}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch {
    if (record.imageUrl) window.open(record.imageUrl, "_blank");
  }
}

/* ─── Animated Stat ─── */
function AnimatedStat({ value, label, colorClass, bgClass, borderClass, delay }: {
  value: number; label: string; colorClass: string; bgClass: string; borderClass: string; delay: number;
}) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => {
      const duration = 900;
      const start = performance.now();
      const tick = (now: number) => {
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 4);
        setDisplay(Math.round(eased * value));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return (
    <div className={`${bgClass} border ${borderClass} rounded-xl p-5 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-default`}>
      <div className="text-xs text-gray-500 mb-1.5">{label}</div>
      <div className={`text-3xl font-bold ${colorClass} tabular-nums`}>{display.toLocaleString()}</div>
    </div>
  );
}

/* ─── Status Badge ─── */
function getStageInfo(
  stage?: AUGCRecord["stage"],
  status?: AUGCRecord["status"],
  validationPolicyId?: string,
  provider?: string
): { label: string; class: string; isProcessing: boolean; icon?: string } {
  const effectiveStage: NonNullable<AUGCRecord["stage"]> =
    stage ||
    (status === "success"
      ? "published"
      : status === "failed"
      ? "failed"
      : "provider_running");

  const isWan = validationPolicyId === "wan-photo-v1" || provider === "wan";

  switch (effectiveStage) {
    case "cutout_queued":
      return {
        label: "生成前抠图排队",
        class: "bg-violet-50 text-violet-700 border-violet-200",
        isProcessing: true,
      };
    case "cutout_processing":
      return {
        label: "生成前抠图处理中",
        class: "bg-purple-50 text-purple-700 border-purple-200",
        isProcessing: true,
      };
    case "provider_queued":
      return {
        label: "待处理",
        class: "bg-slate-50 text-slate-700 border-slate-200",
        isProcessing: true,
      };
    case "provider_running":
      return {
        label: "生成中",
        class: "bg-sky-50 text-sky-700 border-sky-200",
        isProcessing: true,
      };
    case "provider_retry_wait":
      return {
        label: "生成重试等待",
        class: "bg-amber-50 text-amber-700 border-amber-200",
        isProcessing: true,
      };
    case "result_pending":
      return {
        label: isWan ? "待校验" : "待结果处理",
        class: "bg-indigo-50 text-indigo-700 border-indigo-200",
        isProcessing: true,
      };
    case "result_processing":
      return {
        label: isWan ? "校验处理中" : "结果处理中",
        class: "bg-blue-50 text-blue-700 border-blue-200",
        isProcessing: true,
      };
    case "generation_retry_queued":
      return {
        label: "校验未通过重试排队",
        class: "bg-orange-50 text-orange-700 border-orange-200",
        isProcessing: true,
      };
    case "published":
      return {
        label: "成功",
        class: "bg-emerald-50 text-emerald-700 border-emerald-200",
        isProcessing: false,
        icon: "ri-check-line",
      };
    case "failed":
      return {
        label: "失败",
        class: "bg-red-50 text-red-700 border-red-200",
        isProcessing: false,
        icon: "ri-close-line",
      };
    default:
      return {
        label: "处理中",
        class: "bg-sky-50 text-sky-700 border-sky-200",
        isProcessing: true,
      };
  }
}

function StatusBadge({
  status,
  stage,
  validationPolicyId,
  provider,
  regenerating = false,
}: {
  status: AUGCRecord["status"];
  stage?: AUGCRecord["stage"];
  validationPolicyId?: string;
  provider?: string;
  regenerating?: boolean;
}) {
  if (regenerating) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200">
        <i className="ri-loader-4-line animate-spin" />
        重新生成中
      </span>
    );
  }
  const s = getStageInfo(stage, status, validationPolicyId, provider);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${s.class}`}>
      {s.isProcessing ? <i className="ri-loader-4-line animate-spin" /> : null}
      {!s.isProcessing && s.icon ? <i className={s.icon} /> : null}
      {s.label}
    </span>
  );
}

/* ─── Record Detail Drawer ─── */
function RecordDetailDrawer({ record, onClose }: { record: AUGCRecord | null; onClose: () => void }) {
  if (!record) return null;
  const isPublished = (record.stage ? record.stage === "published" : record.status === "success") && Boolean(record.imageUrl);
  const hasFaceScore = typeof record.faceScore === "number";
  const hasValidationScore = typeof record.validationScore === "number";

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto animate-[slideIn_0.3s_ease-out]">
        <style>{`@keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}</style>
        <div className="relative h-72 overflow-hidden bg-slate-900">
          {isPublished ? (
            <img src={record.imageUrl} alt={record.templateName} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-slate-800 flex flex-col items-center justify-center text-slate-400">
              <i className={record.status === "processing" ? "ri-loader-4-line text-4xl animate-spin text-teal-400 mb-2" : "ri-image-line text-4xl text-slate-500 mb-2"} />
              <span className="text-xs text-slate-300 font-medium">
                {record.stage === "published" || record.status === "success" ? "暂无预览图" : "生成结果未发布"}
              </span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30 pointer-events-none" />
          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white/90 rounded-full hover:bg-white transition-colors">
            <i className="ri-close-line text-gray-700" />
          </button>
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-white/90 text-gray-800">
                {record.templateType === "style" ? "风格模板" : "纯换脸"}
              </span>
              <StatusBadge
                status={record.status}
                stage={record.stage}
                validationPolicyId={record.validationPolicyId}
                provider={record.provider}
                regenerating={record.regenerating}
              />
            </div>
            <h2 className="text-xl font-bold text-white">{record.templateName}</h2>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">手机箱号</div>
              <div className="text-sm font-medium text-gray-900">{record.userName}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">生成时间</div>
              <div className="text-sm font-medium text-gray-900">{record.createdAt}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">耗时</div>
              <div className="text-sm font-medium text-gray-900">{record.status === "processing" ? "--" : `${Number(record.generateTime).toFixed(1)} 秒`}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 mb-1">人脸评分</div>
              <div className="text-sm font-medium text-gray-900">
                {hasFaceScore ? (
                  <span className={`${record.faceScore >= 90 ? "text-emerald-600" : record.faceScore >= 70 ? "text-amber-600" : "text-red-600"}`}>
                    {record.faceScore} 分
                  </span>
                ) : hasValidationScore ? (
                  <span className={`${record.validationScore >= 0.4 ? "text-emerald-600" : "text-red-600"}`}>
                    {Math.round(record.validationScore * 100)} 分
                  </span>
                ) : "--"}
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-1.5">
              <i className="ri-shield-check-line text-teal-600" />
              执行与校验信息
            </h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Provider</span>
                <span className="text-gray-900 font-medium">
                  {record.provider === "wan"
                    ? "Wan"
                    : record.provider === "facefusion"
                    ? "FaceFusion"
                    : record.provider || "--"}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-500 shrink-0">执行模型</span>
                <span className="text-gray-900 font-mono text-xs break-all">{record.executionOptions?.model ?? "历史记录未保存"}</span>
              </div>
              {record.executionOptions && isQwenModel(record.executionOptions.model) && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">Qwen 提示词改写</span>
                  <span className="text-gray-900">{record.executionOptions.promptExtend === false ? "关闭" : "开启"}</span>
                </div>
              )}
              {(record.pipeline === "wan" || record.provider === "wan" || record.validationPolicyId === "wan-photo-v1") && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">抠像参考图策略</span>
                  <span className="text-gray-900">{typeof record.useCutout === "boolean" ? record.useCutout ? "开启" : "关闭" : "未记录"}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-gray-500">生成尝试</span>
                <span className="text-gray-900 font-medium">
                  {record.generationAttempt !== undefined
                    ? `${record.generationAttempt} / ${record.maxGenerationAttempts ?? 3}`
                    : "--"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">校验策略</span>
                <span className="text-gray-900 font-mono text-xs bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200">
                  {record.validationPolicyId || "--"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">相似度得分 / 阈值</span>
                <span className="text-gray-900 font-medium">
                  {hasValidationScore
                    ? `${record.validationScore.toFixed(4)} / ${(typeof record.validationThreshold === "number" ? record.validationThreshold : 0.4).toFixed(4)}`
                    : "--"}
                </span>
              </div>
              {(record.lastError || record.regenerationError || record.regenerateUnavailableReason) && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">失败原因</div>
                  <div className="text-xs font-mono text-red-600 bg-red-50 p-2.5 rounded-lg border border-red-100 break-all leading-relaxed">
                    {record.lastError || record.regenerationError || record.regenerateUnavailableReason}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">模板信息</h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">模板名称</span>
                <span className="text-gray-900 font-medium">{record.templateName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">模板类型</span>
                <span className="text-gray-900">{record.templateType === "style" ? "风格模板" : "纯换脸场景"}</span>
              </div>
              {record.styleName && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">所属风格</span>
                  <span className="text-gray-900">{record.styleName}</span>
                </div>
              )}
              {record.sceneType && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-500">场景类型</span>
                  <span className="text-gray-900">{record.sceneType}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => downloadRecordPhoto(record)}
              disabled={(record.stage ? record.stage !== "published" : record.status !== "success")}
              className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-download-line" /> 下载图片
            </button>
            <button className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center justify-center gap-2">
              <i className="ri-share-line" /> 分享
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
function AugcHistoryPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState<SearchField>("template");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<"all" | "style" | "pure">("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [previewRecord, setPreviewRecord] = useState<AUGCRecord | null>(null);
  const [regeneratingIds, setRegeneratingIds] = useState<string[]>([]);
  const [regenerateTarget, setRegenerateTarget] = useState<AUGCRecord | null>(null);
  const regenerateDialogRequest = useRef<{ id: string; key?: string } | null>(null);
  const [pendingRegenerations, setPendingRegenerations] = useState(readPendingRegenerations);
  const avatarRequestsInFlight = useRef(new Set<string>());
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [lastKnownTotal, setLastKnownTotal] = useState<{ query: string; total: number } | null>(null);
  const desktopPagerNavRef = useRef<HTMLElement>(null);
  const [desktopPagerWidth, setDesktopPagerWidth] = useState(0);


  useEffect(() => {
    const container = desktopPagerNavRef.current;
    if (!container) return;

    const observer = new ResizeObserver(([entry]) => {
      setDesktopPagerWidth(entry.contentRect.width);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearchQuery(searchQuery.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const recordsPath = useMemo(() => {
    const params = new URLSearchParams({
      page: String(currentPage),
      limit: String(pageSize),
      templateType: filterType,
      status: filterStatus,
      searchField,
    });
    if (debouncedSearchQuery) params.set("q", debouncedSearchQuery);
    return `/faceswap/records?${params.toString()}`;
  }, [currentPage, debouncedSearchQuery, filterType, filterStatus, searchField]);
  const recordsQuery = useMemo(() => {
    const params = new URLSearchParams({
      limit: String(pageSize),
      templateType: filterType,
      status: filterStatus,
      searchField,
    });
    if (debouncedSearchQuery) params.set("q", debouncedSearchQuery);
    return params.toString();
  }, [debouncedSearchQuery, filterType, filterStatus, searchField]);

  const filteredMockRecordsPage: AUGCRecordsPage = useMemo(() => {
    let filtered = [...mockAugcRecords];

    if (filterType !== "all") {
      filtered = filtered.filter((r) => r.templateType === filterType);
    }

    if (filterStatus !== "all") {
      if (filterStatus === "success") {
        filtered = filtered.filter((r) => r.status === "success" || r.stage === "published");
      } else if (filterStatus === "processing") {
        filtered = filtered.filter(
          (r) => r.status === "processing" || (r.stage && r.stage !== "published" && r.stage !== "failed"),
        );
      } else if (filterStatus === "failed") {
        filtered = filtered.filter((r) => r.status === "failed" || r.stage === "failed");
      } else {
        filtered = filtered.filter((r) => r.stage === filterStatus);
      }
    }

    if (debouncedSearchQuery) {
      const q = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter((r) => {
        if (searchField === "template") return r.templateName.toLowerCase().includes(q);
        if (searchField === "session") return r.userId.toLowerCase().includes(q) || r.id.toLowerCase().includes(q);
        if (searchField === "deviceNo") return r.userName.toLowerCase().includes(q);
        return false;
      });
    }

    const start = (currentPage - 1) * pageSize;
    const items = filtered.slice(start, start + pageSize);
    return {
      items,
      total: filtered.length,
      page: currentPage,
      limit: pageSize,
    };
  }, [currentPage, pageSize, filterType, filterStatus, searchField, debouncedSearchQuery]);

  const { data: apiRecordsPage, dataPath, loading: recordsLoading, reload: reloadRecords } = useApi<AUGCRecordsPage>(
    recordsPath,
    filteredMockRecordsPage,
  );
  const reloadRecordsRef = useRef(reloadRecords);

  useEffect(() => {
    if (dataPath !== recordsPath) return;

    setLastKnownTotal((previous) =>
      previous?.query === recordsQuery && previous.total === apiRecordsPage.total
        ? previous
        : { query: recordsQuery, total: apiRecordsPage.total },
    );
  }, [apiRecordsPage, dataPath, recordsPath, recordsQuery]);


  const isApiDataCurrent = dataPath === recordsPath;
  const isLoadingPage = !isApiDataCurrent && (recordsLoading || dataPath !== null);
  const fallbackTotal =
    lastKnownTotal?.query === recordsQuery ? lastKnownTotal.total : mockRecordsPage.total;
  const recordsPage = isApiDataCurrent
    ? apiRecordsPage
    : { items: [], total: fallbackTotal, page: currentPage, limit: pageSize };
  const pageRecords = recordsPage.items;
  const totalPages = Math.max(1, Math.ceil(recordsPage.total / pageSize));
  const [jumpPage, setJumpPage] = useState(String(currentPage));

  useEffect(() => {
    setJumpPage(String(currentPage));
  }, [currentPage]);

  const handleJumpCommit = () => {
    const trimmed = jumpPage.trim();
    if (trimmed !== "" && !isNaN(Number(trimmed))) {
      const parsed = Math.floor(Number(trimmed));
      const clamped = Math.min(totalPages, Math.max(1, parsed));
      setCurrentPage(clamped);
      setJumpPage(String(clamped));
    } else {
      setJumpPage(String(currentPage));
    }
  };


  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    reloadRecordsRef.current = reloadRecords;
  }, [reloadRecords]);

  const changePendingRegeneration = (id: string, pending: PendingRegeneration | null, expectedKey?: string): boolean => {
    if (!persistPendingRegeneration(id, pending, expectedKey)) return false;
    if (pending && regenerateDialogRequest.current?.id === id) {
      regenerateDialogRequest.current = { id, key: pending.key };
    }
    setPendingRegenerations((entries) => {
      if (!pending && entries[id]?.key !== expectedKey) return entries;
      const next = { ...entries };
      if (pending) next[id] = pending;
      else delete next[id];
      return next;
    });
    return true;
  };

  const closeAcceptedDialog = (id: string, key: string) => {
    const request = regenerateDialogRequest.current;
    if (!request || request.id !== id || request.key !== key) return;
    regenerateDialogRequest.current = null;
    setRegenerateTarget(null);
  };

  const acceptRegeneration = (id: string, key: string) => {
    if (!changePendingRegeneration(id, null, key)) return;
    closeAcceptedDialog(id, key);
    setRegeneratingIds((ids) => ids.includes(id) ? ids : [...ids, id]);
    toast("已确认本次重生成，任务已进入队列", "success");
    reloadRecords();
    window.setTimeout(() => reloadRecordsRef.current(), 3000);
  };

  const regenerateRecord = async (record: AUGCRecord) => {
    const pending = pendingRegenerations[record.id];
    if (avatarRequestsInFlight.current.has(record.id) || (!pending && (!record.canRegenerate || record.regenerating || regeneratingIds.includes(record.id)))) return;
    if (pending?.body.executionOptions || typeof pending?.body.useCutout === "boolean" || record.pipeline === "wan" || (!record.pipeline && (record.provider === "wan" || record.validationPolicyId === "wan-photo-v1"))) {
      regenerateDialogRequest.current = { id: record.id, key: pending?.key };
      setRegenerateTarget(record);
      return;
    }
    // Avatar keeps its original pipeline and confirmation; no cloud options.
    const action = record.status === "failed" ? "重新尝试生成" : "重新生成";
    const message = pending
      ? "上次提交结果尚未确认，将使用原请求重试确认，不会新建另一条任务。继续吗？"
      : `将先验证原自拍、模板和生成所需资源可用；验证通过后才会${action}。验证失败会保留当前结果不变。继续吗？`;
    if (!window.confirm(message)) return;
    const tuple: PendingRegeneration = pending ?? {
      key: crypto.randomUUID(),
      body: { expectedAttemptId: record.currentAttemptId ?? record.id },
    };
    avatarRequestsInFlight.current.add(record.id);
    let persisted = Boolean(pending);
    try {
      if (!pending) changePendingRegeneration(record.id, tuple);
      persisted = true;
      const response = await apiSend<RegenerationResponse>("POST", `/faceswap/records/${record.id}/regenerate`, tuple.body, { headers: { "Idempotency-Key": tuple.key } });
      if (!response.jobId || (response.retryIdempotencyKey && response.retryIdempotencyKey !== tuple.key)) throw new Error("服务端未确认本次请求");
      acceptRegeneration(record.id, tuple.key);
    } catch (err) {
      if (!persisted) {
        toast(`无法保存待确认请求，未发送生成任务：${err instanceof Error ? err.message : String(err)}`, "error");
      } else if (err instanceof ApiError && err.status < 500 && err.status !== 408 && !(pending && (err.status === 401 || err.status === 403))) {
        changePendingRegeneration(record.id, null, tuple.key);
        toast(`无法重新生成：${err.message}。当前结果未变。`, "error");
      } else {
        toast(`提交结果尚未确认：${err instanceof Error ? err.message : String(err)}。请重试确认同一请求。`, "error");
      }
    } finally {
      avatarRequestsInFlight.current.delete(record.id);
    }
  };

  useEffect(() => {
    if (!isApiDataCurrent) return;
    for (const record of pageRecords) {
      const pending = pendingRegenerations[record.id];
      if (pending && record.retryIdempotencyKey === pending.key && changePendingRegeneration(record.id, null, pending.key)) {
        closeAcceptedDialog(record.id, pending.key);
        toast("已从服务端记录确认本次重生成请求", "success");
      }
    }
  }, [pageRecords, isApiDataCurrent, pendingRegenerations]);

  useEffect(() => {
    const active = pageRecords.filter((record) => record.regenerating).map((record) => record.id);
    setRegeneratingIds(active);
    if (active.length === 0 && Object.keys(pendingRegenerations).length === 0) return;
    const timer = window.setInterval(() => reloadRecordsRef.current(), 3000);
    return () => window.clearInterval(timer);
  }, [pageRecords, pendingRegenerations]);

  return (
    <div className="space-y-5">
      {/* Header (hidden when embedded inside 模板管理) */}
      <div className={`flex items-center justify-between ${embedded ? "hidden" : ""}`}>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">生成记录</h1>
          <p className="text-sm text-gray-500 mt-1">AUGC 换脸生成历史记录与效果追踪</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-download-line" /> 导出数据
          </button>
        </div>
      </div>



      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <label htmlFor="augc-history-search-field" className="text-sm text-gray-500 whitespace-nowrap">
            搜索字段:
          </label>
          <select
            id="augc-history-search-field"
            value={searchField}
            onChange={(e) => {
              setSearchField(e.target.value as SearchField);
              setCurrentPage(1);
            }}
            className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
          >
            {searchFieldOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <div className="relative flex-1 min-w-56">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchFieldOptions.find((option) => option.value === searchField)?.placeholder}
              className="w-full pl-9 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500">模板类型:</span>
            {(["all", "style", "pure"] as const).map((t) => (
              <button
                key={t}
                onClick={() => { setFilterType(t); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${filterType === t ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {t === "all" ? "全部" : t === "style" ? "风格模板" : "纯换脸"}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-500">状态:</span>
            {[
              { value: "all", label: "全部" },
              { value: "success", label: "成功" },
              { value: "processing", label: "处理中" },
              { value: "cutout_queued", label: "生成前抠图排队" },
              { value: "cutout_processing", label: "生成前抠图处理中" },
              { value: "provider_queued", label: "待处理" },
              { value: "provider_running", label: "生成中" },
              { value: "provider_retry_wait", label: "生成重试等待" },
              { value: "result_pending", label: "待结果处理 / 待校验" },
              { value: "result_processing", label: "结果处理中 / 校验处理中" },
              { value: "generation_retry_queued", label: "校验重试排队" },
              { value: "failed", label: "失败" },
            ].map((s) => (
              <button
                key={s.value}
                onClick={() => { setFilterStatus(s.value); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${filterStatus === s.value ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left font-medium text-gray-700">缩略图</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">手机箱号</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">模板名称</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">类型</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">生成时间</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">状态</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">耗时</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">人脸评分</th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100" aria-busy={isLoadingPage}>
              {isLoadingPage && Array.from({ length: pageSize }, (_, index) => (
                <tr key={`records-loading-${index}`} className="h-20 animate-pulse">
                  <td colSpan={9} className="px-4 py-3">
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-12 shrink-0 rounded-lg bg-gray-100" />
                      <div className="h-8 flex-1 rounded bg-gray-100" />
                      <div className="h-8 w-24 rounded bg-gray-100" />
                      <div className="h-8 w-20 rounded bg-gray-100" />
                      <div className="h-8 w-28 rounded bg-gray-100" />
                    </div>
                  </td>
                </tr>
              ))}
              {pageRecords.map((record) => (
                <tr key={record.id} onClick={() => setPreviewRecord(record)} className="h-20 hover:bg-gray-50 transition-colors cursor-pointer">
                  <td className="px-4 py-3">
                    <div className="w-12 h-14 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                      {(record.stage ? record.stage === "published" : record.status === "success") && record.imageUrl ? (
                        <img src={record.imageUrl} alt={record.templateName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50">
                          <i className={record.status === "processing" ? "ri-loader-4-line text-lg animate-spin text-teal-600" : "ri-image-line text-lg text-gray-300"} />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{record.userName}</div>
                    <div className="text-xs text-gray-500">{record.userId}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{record.templateName}</div>
                    <div className="text-xs text-gray-500">
                      {record.styleName || record.sceneType || "--"}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${record.templateType === "style" ? "bg-violet-50 text-violet-700" : "bg-teal-50 text-teal-700"}`}>
                      {record.templateType === "style" ? "风格模板" : "纯换脸"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{record.createdAt}</td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={record.status}
                      stage={record.stage}
                      validationPolicyId={record.validationPolicyId}
                      provider={record.provider}
                      regenerating={record.regenerating || regeneratingIds.includes(record.id)}
                    />
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {record.status === "processing" ? "--" : `${Number(record.generateTime).toFixed(1)}s`}
                  </td>
                  <td className="px-4 py-3">
                    {typeof record.faceScore === "number" ? (
                      <span className={`text-xs font-medium ${record.faceScore >= 90 ? "text-emerald-600" : record.faceScore >= 70 ? "text-amber-600" : "text-red-600"}`}>
                        {record.faceScore} 分
                      </span>
                    ) : typeof record.validationScore === "number" ? (
                      <span className={`text-xs font-medium ${record.validationScore >= 0.4 ? "text-emerald-600" : "text-red-600"}`}>
                        {Math.round(record.validationScore * 100)} 分
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">--</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setPreviewRecord(record)}
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                        title="查看详情"
                      >
                        <i className="ri-eye-line" />
                      </button>
                      <button
                        onClick={() => downloadRecordPhoto(record)}
                        disabled={(record.stage ? record.stage !== "published" : record.status !== "success")}
                        className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="下载"
                      >
                        <i className="ri-download-line" />
                      </button>
                      {(record.status === "success" || record.status === "failed" || pendingRegenerations[record.id]) && (
                        <button
                          onClick={() => void regenerateRecord(record)}
                          disabled={!pendingRegenerations[record.id] && (!record.canRegenerate || record.regenerating || regeneratingIds.includes(record.id))}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          title={pendingRegenerations[record.id] ? "确认上次重生成请求" : record.regenerating || regeneratingIds.includes(record.id) ? "重新生成中" : record.regenerateUnavailableReason || "按原自拍和模板重新生成"}
                        >
                          <i className={record.regenerating || regeneratingIds.includes(record.id) ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!isLoadingPage && pageRecords.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-image-line text-4xl mb-3 block" />
            <p className="text-sm">没有找到符合条件的记录</p>
          </div>
        )}

        {/* Pagination */}
        <div className="flex flex-col gap-3 px-4 py-3 border-t border-gray-200 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-gray-500">
            共 {recordsPage.total} 条记录，第 {currentPage} / {totalPages} 页
          </span>
          <nav ref={desktopPagerNavRef} aria-label="分页导航" className="flex-1 min-w-0">
            {/* Compact pager for narrow containers */}
            <div className={`flex items-center gap-2 ${desktopPagerWidth >= 480 ? "hidden" : ""}`}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="上一页"
                title="上一页"
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              <span className="text-xs text-gray-600 px-1 font-medium">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="下一页"
                title="下一页"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>

            {/* Desktop Pager */}
            <div className={`${desktopPagerWidth >= 480 ? "flex" : "hidden"} items-center justify-end gap-1.5`}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="上一页"
                title="上一页"
              >
                <i className="ri-arrow-left-s-line" />
              </button>

              {pageWindow(
                currentPage,
                totalPages,
                Math.max(
                  5,
                  Math.min(11, Math.floor((desktopPagerWidth - 156) / 38)),
                ),
              ).map((page, index) =>
                page === "ellipsis" ? (
                  <span
                    key={`ellipsis-${index}`}
                    className="w-8 h-8 flex items-center justify-center text-gray-400 text-xs"
                    aria-hidden="true"
                  >
                    …
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-colors ${
                      currentPage === page
                        ? "bg-teal-600 text-white"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                    aria-current={currentPage === page ? "page" : undefined}
                  >
                    {page}
                  </button>
                )
              )}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="下一页"
                title="下一页"
              >
                <i className="ri-arrow-right-s-line" />
              </button>
              <div className="flex items-center gap-1.5 text-xs text-gray-500 ml-2">
                <label htmlFor="jump-page-input" className="whitespace-nowrap">
                  跳至
                </label>
                <input
                  id="jump-page-input"
                  type="text"
                  value={jumpPage}
                  onChange={(e) => setJumpPage(e.target.value)}
                  onBlur={handleJumpCommit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleJumpCommit();
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  className="w-11 h-8 px-1 text-center bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  aria-label="跳转页码"
                />
                <span className="whitespace-nowrap">页</span>
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* Detail Drawer */}
      <RecordDetailDrawer record={previewRecord} onClose={() => setPreviewRecord(null)} />
      {regenerateTarget && (
        <RegenerateRecordDialog
          key={regenerateTarget.id}
          record={pageRecords.find((record) => record.id === regenerateTarget.id) ?? regenerateTarget}
          pending={pendingRegenerations[regenerateTarget.id]}
          onPendingChange={changePendingRegeneration}
          onAccepted={acceptRegeneration}
          onClose={() => { regenerateDialogRequest.current = null; setRegenerateTarget(null); }}
        />
      )}
    </div>
  );
}

export default AugcHistoryPage;
